import { CheckCircle, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Textarea } from "../../../components/ui/textarea";
import {
  AvailableLangs,
  availableLanguages,
  ISO639Lang,
} from "../../../constans/languagesList";

interface ReviewMessageModalProps {
  isApproval: boolean;
  specialistName: string;
  specializationLabel?: string;
  rejectedFields?: string;
  onSend: (message: string) => Promise<void>;
  onCancel: () => void;
  userSelectedLang?: AvailableLangs;
  approvalTemplatesProps?: {
    [key in AvailableLangs]?: (name: string, spec?: string) => string;
  };
  rejectionTemplatesProps?: {
    [key in AvailableLangs]?: (
      name: string,
      spec?: string,
      fields?: string,
    ) => string;
  };
}

const approvalTemplates: Record<
  AvailableLangs,
  (name: string, spec?: string) => string
> = {
  en: (name: string, spec?: string) => `Dear ${name},

Congratulations! Your profile${
    spec ? ` as ${spec}` : ""
  } has been reviewed and approved.

Your profile is now live and visible to potential clients.

Thank you for being part of our platform!

Best regards,
Review Team`,
  ru: (name: string, spec?: string) => `Уважаемый(ая) ${name},

Поздравляем! Ваш профиль${spec ? ` ${spec}` : ""} был проверен и одобрен.

Теперь ваш профиль виден потенциальным клиентам.

Спасибо, что вы с нами! Приглашаем вас в чат Talzi в Telegram: 
https://t.me/+Ea1fhi-5Bp41ZDNi

С уважением,
Команда модерации`,
  uk: (name: string, spec?: string) => `Шановний(а) ${name},

Вітаємо! Ваш профіль${spec ? ` ${spec}` : ""} було перевірено та схвалено.

Тепер ваш профіль видно потенційним клієнтам.

Дякуємо, що ви з нами! Запрошуємо вас до чату Talzi в Telegram: 
https://t.me/+-sPa216AuHw1N2Zi

З повагою,
Команда модерації`,
};

const rejectionTemplates: Record<
  AvailableLangs,
  (name: string, spec?: string, fields?: string) => string
> = {
  en: (name: string, spec?: string, fields?: string) => {
    let msg = `Dear ${name},

Thank you for submitting your profile${spec ? ` as ${spec}` : ""} for review.

Unfortunately, we cannot approve your profile at this time. Please review the feedback below and make the necessary changes before resubmitting:

`;
    if (fields) {
      msg += fields + "\n\n";
    } else {
      msg += "[Please provide specific reasons for rejection]\n\n";
    }
    msg += `Once you've addressed these issues, feel free to resubmit your profile for review.

Best regards,
Review Team`;
    return msg;
  },
  ru: (name: string, spec?: string, fields?: string) => {
    let msg = `Уважаемый(ая) ${name},

Спасибо за отправку вашего профиля${spec ? ` как ${spec}` : ""} на модерацию.

К сожалению, мы не можем одобрить ваш профиль в данный момент. Пожалуйста, ознакомьтесь с замечаниями ниже и внесите необходимые изменения перед повторной отправкой:

`;
    if (fields) {
      msg += fields + "\n\n";
    } else {
      msg += "[Пожалуйста, укажите конкретные причины отклонения]\n\n";
    }
    msg += `После внесения изменений вы можете повторно отправить профиль на модерацию.

С уважением,
Команда модерации`;
    return msg;
  },
  uk: (name: string, spec?: string, fields?: string) => {
    let msg = `Вітаємо!

Дякуємо за подання вашого профілю${spec ? ` як ${spec}` : ""} на перевірку.

На жаль, ми не можемо схвалити ваш профіль зараз. Будь ласка, ознайомтеся з зауваженнями нижче та внесіть необхідні зміни перед повторною відправкою:

`;
    if (fields) {
      msg += fields + "\n\n";
    } else {
      msg += "[Будь ласка, вкажіть конкретні причини відхилення]\n\n";
    }
    msg += `Після внесення змін ви можете повторно подати профіль на перевірку.

З повагою,
Команда модерації`;
    return msg;
  },
};
const additionalNotesLabel: Record<AvailableLangs, string> = {
  en: "--- Additional Notes ---",
  ru: "--- Дополнительные заметки ---",
  uk: "--- Додаткові нотатки ---",
};
export const ReviewMessageModal = ({
  isApproval,
  specialistName,
  specializationLabel,
  rejectedFields,
  onSend,
  onCancel,
  userSelectedLang = "en",
  approvalTemplatesProps,
  rejectionTemplatesProps,
}: ReviewMessageModalProps) => {
  const [selectedLang, setSelectedLang] =
    useState<AvailableLangs>(userSelectedLang);

  const getDefaultApprovalMessage = () =>
    (approvalTemplatesProps?.[selectedLang] || approvalTemplates[selectedLang])(
      specialistName,
      specializationLabel,
    );

  const getDefaultRejectionMessage = () =>
    (
      rejectionTemplatesProps?.[selectedLang] ||
      rejectionTemplates[selectedLang]
    )(specialistName, specializationLabel, rejectedFields);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isSending, setIsSending] = useState(false);

  const [baseMessage, setBaseMessage] = useState(
    isApproval ? getDefaultApprovalMessage() : getDefaultRejectionMessage(),
  );

  useEffect(() => {
    setBaseMessage(
      isApproval ? getDefaultApprovalMessage() : getDefaultRejectionMessage(),
    );
    // eslint-disable-next-line
  }, [
    selectedLang,
    isApproval,
    specialistName,
    specializationLabel,
    rejectedFields,
  ]);

  const finalMessage = additionalNotes
    ? `${baseMessage}\n\n${additionalNotesLabel[selectedLang]}\n${additionalNotes}`
    : baseMessage;

  const handleSend = async () => {
    setIsSending(true);
    try {
      await onSend(finalMessage);
    } finally {
      setIsSending(false);
    }
  };

  const handleUseDefault = () => {
    setBaseMessage(
      isApproval ? getDefaultApprovalMessage() : getDefaultRejectionMessage(),
    );
  };

  // Keyboard shortcut: Ctrl+Enter or Cmd+Enter to send
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (
          !isSending &&
          !(
            !isApproval &&
            finalMessage.includes(
              selectedLang === "ru"
                ? "[Пожалуйста, укажите конкретные причины отклонения]"
                : selectedLang === "uk"
                  ? "[Будь ласка, вкажіть конкретні причини відхилення]"
                  : "[Please provide specific reasons for rejection]",
            )
          )
        ) {
          handleSend();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line
  }, [finalMessage, isSending, isApproval, selectedLang]);

  const rejectionPlaceholder =
    selectedLang === "ru"
      ? "[Пожалуйста, укажите конкретные причины отклонения]"
      : selectedLang === "uk"
        ? "[Будь ласка, вкажіть конкретні причини відхилення]"
        : "[Please provide specific reasons for rejection]";

  return (
    <div className="dark">
      <Card className="bg-gray-800 border-gray-700 w-[600px]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              {isApproval ? (
                <>
                  <CheckCircle className="text-green-400" size={24} />
                  Approve Profile
                </>
              ) : (
                <>
                  <XCircle className="text-red-400" size={24} />
                  Reject Profile
                </>
              )}
            </CardTitle>
            <Badge variant={isApproval ? "default" : "destructive"}>
              {isApproval ? "Approval" : "Rejection"}
            </Badge>
          </div>
          <div className="flex gap-2 mt-2 items-center">
            <span className="text-xs text-gray-400">Message language:</span>
            <select
              className="bg-gray-900 border border-gray-700 text-white text-xs rounded px-2 py-1"
              value={selectedLang}
              onChange={(e) =>
                setSelectedLang(e.target.value as AvailableLangs)
              }
            >
              {availableLanguages.map((lang) => (
                <option value={lang} key={lang}>
                  {lang.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 mt-2 items-center">
            <p className="text-sm text-gray-400 mt-2">
              User language:{" "}
              <span className="text-white">{ISO639Lang[userSelectedLang]}</span>
            </p>
          </div>
          {specializationLabel && (
            <p className="text-sm text-gray-400 mt-2">
              Specialization:{" "}
              <span className="text-white">{specializationLabel}</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-gray-300 mb-3">
              {isApproval
                ? "Send an approval notification to the specialist with optional custom message."
                : "Send a rejection notification with explanation of what needs to be fixed."}
            </p>
          </div>

          {/* Base Message Editor */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-white">
                Base Message
              </label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUseDefault}
                  disabled={
                    baseMessage ===
                    (isApproval
                      ? getDefaultApprovalMessage()
                      : getDefaultRejectionMessage())
                  }
                >
                  Reset to Default
                </Button>
              </div>
            </div>
            <Textarea
              value={baseMessage}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setBaseMessage(e.target.value)
              }
              className="min-h-[250px] bg-gray-900 border-gray-600 text-white resize-none font-mono text-sm"
              placeholder="Type your message here..."
            />
            <p className="text-xs text-gray-400">
              {baseMessage.length} characters
              {!isApproval && baseMessage.includes(rejectionPlaceholder) && (
                <span className="text-yellow-400 ml-2">
                  ⚠ Please replace the placeholder text with specific feedback
                </span>
              )}
            </p>
          </div>

          {/* Additional Notes (Auto-Appended) */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">
              Additional Notes (Auto-appended)
            </label>
            <Textarea
              value={additionalNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setAdditionalNotes(e.target.value)
              }
              className="min-h-[80px] bg-gray-900 border-gray-600 text-white resize-none text-sm"
              placeholder="Add any extra notes (will be automatically appended to the message)..."
            />
            {additionalNotes && (
              <p className="text-xs text-green-400">
                ✓ These notes will be appended automatically to the message
                above
              </p>
            )}
          </div>

          {/* Final Message Preview */}
          {additionalNotes && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-300">
                Final Message Preview
              </label>
              <div className="p-3 bg-gray-950 border border-gray-700 rounded-md text-xs text-gray-300 whitespace-pre-wrap font-mono max-h-[200px] overflow-y-auto">
                {finalMessage}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <Button
              variant="ghost"
              onClick={onCancel}
              disabled={isSending}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant={isApproval ? "default" : "destructive"}
              onClick={handleSend}
              disabled={
                isSending ||
                (!isApproval && finalMessage.includes(rejectionPlaceholder))
              }
              className="flex-1"
            >
              {isSending ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  {isApproval
                    ? "✓ Approve & Send Message"
                    : "✗ Reject & Send Message"}
                  <span className="text-xs ml-2 opacity-60">(Ctrl+Enter)</span>
                </>
              )}
            </Button>
          </div>

          {!isApproval && finalMessage.includes(rejectionPlaceholder) && (
            <p className="text-xs text-yellow-400 text-center">
              Please provide specific reasons for rejection before sending
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
